import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { 
  Mail, 
  ArrowLeft, 
  Check, 
  Building2, 
  User, 
  MessageSquare,
  Shield,
  Sun,
  Moon,
  MapPin,
  Clock
} from "lucide-react";
import { useTheme } from "../App";
import { toast } from "sonner";

const translations = {
  fr: {
    badge: "Parler à un conseiller",
    title: "Contactez notre équipe",
    subtitle: "Une question sur ALIA ? Notre équipe commerciale est à votre disposition pour répondre à vos questions et vous accompagner.",
    form: {
      name: "Nom complet",
      email: "Email professionnel",
      company: "Entreprise",
      subject: "Sujet",
      subjectOptions: [
        "Question commerciale",
        "Demande de devis",
        "Partenariat",
        "Support technique",
        "Autre"
      ],
      message: "Votre message",
      messagePlaceholder: "Comment pouvons-nous vous aider ?",
      submit: "Envoyer le message",
      submitting: "Envoi en cours..."
    },
    contact: {
      title: "Contactez-nous",
      email: "contact@alia-assistant.com",
      hours: "Lun-Ven, 9h-18h (CET)",
      address: "France"
    },
    trust: "14 jours d'essai gratuit, sans carte bancaire",
    success: {
      title: "Message envoyé !",
      message: "Notre équipe vous répondra dans les plus brefs délais."
    },
    back: "Retour à l'accueil"
  },
  en: {
    badge: "Talk to an advisor",
    title: "Contact our team",
    subtitle: "Have a question about ALIA? Our sales team is available to answer your questions and support you.",
    form: {
      name: "Full name",
      email: "Professional email",
      company: "Company",
      subject: "Subject",
      subjectOptions: [
        "Sales inquiry",
        "Quote request",
        "Partnership",
        "Technical support",
        "Other"
      ],
      message: "Your message",
      messagePlaceholder: "How can we help you?",
      submit: "Send message",
      submitting: "Sending..."
    },
    contact: {
      title: "Contact us",
      email: "contact@alia-assistant.com",
      hours: "Mon-Fri, 9am-6pm (CET)",
      address: "France"
    },
    trust: "14-day free trial, no credit card required",
    success: {
      title: "Message sent!",
      message: "Our team will get back to you as soon as possible."
    },
    back: "Back to home"
  }
};

const ContactPage = () => {
  const { theme, toggleTheme } = useTheme();
  const [lang, setLang] = useState("fr");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    subject: "",
    message: ""
  });

  const t = translations[lang];
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/leads/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          language: lang
        }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setIsSuccess(true);
        toast.success(t.success.title, { description: data.message || t.success.message });
      } else {
        throw new Error(data.detail || 'Failed to submit');
      }
    } catch (error) {
      console.error('Contact submission error:', error);
      toast.error(lang === 'fr' ? 'Erreur lors de l\'envoi' : 'Submission error', {
        description: lang === 'fr' ? 'Veuillez réessayer.' : 'Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#020617]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-slate-200 dark:border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo-alia.png" style={{height: "80px", objectFit: "contain"}} alt="ALIA Assistant" />
            </Link>

            <div className="flex items-center gap-3">
              <div className="flex items-center border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                <button
                  onClick={() => setLang("fr")}
                  className={`px-2.5 py-1.5 text-sm font-medium transition-colors ${
                    lang === "fr" 
                      ? "bg-blue-600 text-white" 
                      : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                  }`}
                >
                  FR
                </button>
                <button
                  onClick={() => setLang("en")}
                  className={`px-2.5 py-1.5 text-sm font-medium transition-colors ${
                    lang === "en" 
                      ? "bg-blue-600 text-white" 
                      : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                  }`}
                >
                  EN
                </button>
              </div>
              <button 
                onClick={toggleTheme}
                className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              >
                {theme === "dark" ? (
                  <Sun className="w-5 h-5 text-slate-400" />
                ) : (
                  <Moon className="w-5 h-5 text-slate-600" />
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="pt-24 pb-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-slate-600 dark:text-slate-400 hover:text-blue-600 mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {t.back}
          </Link>

          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Left Column - Info */}
            <div>
              <Badge className="mb-6 bg-blue-600/10 text-blue-600 dark:text-blue-400 border-blue-600/20">
                <MessageSquare className="w-3 h-3 mr-1" />
                {t.badge}
              </Badge>

              <h1 
                className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-6"
                style={{ fontFamily: 'Outfit' }}
              >
                {t.title}
              </h1>

              <p className="text-lg text-slate-600 dark:text-slate-400 mb-8">
                {t.subtitle}
              </p>

              {/* Contact Info */}
              <div className="space-y-6 mb-8">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                  {t.contact.title}
                </h3>
                
                <div className="space-y-4">
                  <div className="flex items-center gap-3 text-slate-600 dark:text-slate-400">
                    <Mail className="w-5 h-5 text-blue-600" />
                    <a href={`mailto:${t.contact.email}`} className="hover:text-blue-600 transition-colors">
                      {t.contact.email}
                    </a>
                  </div>
                  <div className="flex items-center gap-3 text-slate-600 dark:text-slate-400">
                    <Clock className="w-5 h-5 text-blue-600" />
                    <span>{t.contact.hours}</span>
                  </div>
                  <div className="flex items-center gap-3 text-slate-600 dark:text-slate-400">
                    <MapPin className="w-5 h-5 text-blue-600" />
                    <span>{t.contact.address}</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                <Shield className="w-5 h-5 text-emerald-500" />
                <span className="text-emerald-700 dark:text-emerald-300 text-sm font-medium">
                  {t.trust}
                </span>
              </div>
            </div>

            {/* Right Column - Form */}
            <Card className="glass-card p-8">
              {isSuccess ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-green-500/10 flex items-center justify-center">
                    <Check className="w-8 h-8 text-green-500" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2" style={{ fontFamily: 'Outfit' }}>
                    {t.success.title}
                  </h2>
                  <p className="text-slate-600 dark:text-slate-400 mb-6">
                    {t.success.message}
                  </p>
                  <Link to="/">
                    <Button variant="outline">{t.back}</Button>
                  </Link>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                      {t.form.name} *
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="pl-10"
                        data-testid="contact-name"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                      {t.form.email} *
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="pl-10"
                        data-testid="contact-email"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                      {t.form.company}
                    </label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="pl-10"
                        data-testid="contact-company"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                      {t.form.subject} *
                    </label>
                    <select
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      data-testid="contact-subject"
                    >
                      <option value="">--</option>
                      {t.form.subjectOptions.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                      {t.form.message} *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={4}
                      placeholder={t.form.messagePlaceholder}
                      className="w-full px-4 py-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                      data-testid="contact-message"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg"
                    disabled={isSubmitting}
                    data-testid="contact-submit"
                  >
                    {isSubmitting ? t.form.submitting : t.form.submit}
                  </Button>
                </form>
              )}
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ContactPage;
